import { Text, Flex, Link } from '@workwhile/ui'
import { ChevronDown, ChevronRight } from 'lucide-react'
import React from 'react'
import { useBillRatesContextValue } from '../context/BillRatesProvider'
import { ColumnDef } from '@tanstack/react-table'
import { BillRate } from '../types'
import { ActionsCell } from './ActionsCell'
import { ExpandablePaginatedTable } from 'components/Table/ExpandablePaginatedTable'
import { DateWithRelativeTime } from 'components/Table/DateWithRelativeTime'
import { BillRateShiftsProvider } from './BillRateShifts/context/BillRateShiftsProvider'
import { BillRateShiftsMain } from './BillRateShifts/components/BillRateShiftsMain'

const columns: ColumnDef<BillRate>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    size: 180,
    cell: ({ row }) => (
      <Flex alignItems="center" gap={1}>
        <Flex
          alignItems="center"
          justifyContent="center"
          color="lightText"
          role="button"
          tabIndex={0}
          aria-expanded={row.getIsExpanded()}
          aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
          onClick={() => row.toggleExpanded()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              row.toggleExpanded()
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          {row.getIsExpanded() ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
        </Flex>
        <Text>{row.original.id}</Text>
        {row.original.deletedAt !== null ? (
          <Text
            as="span"
            fontSize={0}
            fontWeight={600}
            px={2}
            borderRadius="12px"
            backgroundColor="error"
            color="white"
          >
            inactive
          </Text>
        ) : null}
      </Flex>
    ),
  },
  {
    header: 'Company',
    accessorKey: 'companyName',
    enableResizing: true,
    minSize: 180,
    cell: ({ row }) => {
      return (
        <Text>
          {row.original.company.name} (
          <Link to={`/companies/${row.original.company.id}`}>
            {row.original.company.id}
          </Link>
          )
        </Text>
      )
    },
  },
  {
    header: 'Position',
    accessorKey: 'positionName',
    enableResizing: true,
    minSize: 180,
    cell: ({ row }) => {
      return (
        <Text>
          {row.original.position.name} (
          <Link to={`/position_editor?positionId=${row.original.position.id}`}>
            {row.original.position.id}
          </Link>
          )
        </Text>
      )
    },
  },
  {
    header: () => (
      <Flex alignItems="center" gap={1} width="100px">
        <Text as="span">Pay Rate</Text>
      </Flex>
    ),
    accessorKey: 'payRate',
    minSize: 100,
    maxSize: 150,
    cell: ({ row }) => {
      const payRate = Number(row.original.payRate)
      return `$${payRate.toFixed(2)}`
    },
  },
  {
    header: () => (
      <Flex alignItems="center" gap={1} width="100px">
        <Text as="span">Charge Rate</Text>
      </Flex>
    ),
    accessorKey: 'billRate',
    minSize: 100,
    maxSize: 150,
    cell: ({ row }) => {
      const billRate = Number(row.original.billRate)
      return `$${billRate.toFixed(2)}`
    },
  },
  {
    header: 'Markup',
    accessorKey: 'markup',
    minSize: 80,
    maxSize: 120,
    cell: ({ row }) => {
      const payRate = Number(row.original.payRate)
      const billRate = Number(row.original.billRate)

      if (payRate === 0 || isNaN(payRate) || isNaN(billRate)) {
        return '-'
      }

      const markup = (billRate / payRate - 1) * 100
      const isNegative = markup < 0

      return (
        <Text color={isNegative ? 'error' : undefined}>
          {markup.toFixed(2)}%
        </Text>
      )
    },
  },
  {
    header: 'Created At',
    accessorKey: 'createdAt',
    enableResizing: true,
    size: 160,
    cell: ({ row }) => <DateWithRelativeTime date={row.original.createdAt} />,
  },
  {
    header: 'Updated At',
    accessorKey: 'updatedAt',
    enableResizing: true,
    size: 160,
    cell: ({ row }) => <DateWithRelativeTime date={row.original.updatedAt} />,
  },
  {
    header: 'Actions',
    accessorKey: 'actions',
    size: 70,
    cell: ({ row }) =>
      row.original.deletedAt === null ? (
        <ActionsCell billRate={row.original} />
      ) : null,
  },
]

export const BillRatesTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useBillRatesContextValue()

  return (
    <ExpandablePaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No bill rates found."
      emptyStateMaxWidth={['100%', '100%', '50%', '50%']}
      getRowCanExpand={() => true}
      showExpandColumn={false}
      expandOnRowClick
      getRowOpacity={(billRate) => (billRate.deletedAt === null ? 1 : 0.5)}
      renderExpandedContent={(billRate) => (
        <BillRateShiftsProvider billRateId={billRate.id}>
          <BillRateShiftsMain />
        </BillRateShiftsProvider>
      )}
    />
  )
}
